import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Volume2, VolumeX, Bell } from 'lucide-react';
import LazyReactPlayer from '../components/LazyReactPlayer';
import LazyMovieCard from '../components/LazyMovieCard';
import { getRecentlyViewedCollection, hasRecentlyViewed } from '../utils/recentlyViewed';
import SEO from '../components/SEO';

// Lazy load non-critical components
const ModalPlayer = lazy(() => import('../components/ModalPlayer'));
const MovieModal = lazy(() => import('../components/MovieModal'));
const NotifyMeModal = lazy(() => import('../components/NotifyMeModal'));

// Utility function to format duration without seconds
const formatDuration = (duration) => {
  if (!duration) return '2h 10m';
  
  // If it's already in a good format (like "2h 10m"), return as is
  if (typeof duration === 'string' && duration.includes('h') && duration.includes('m')) {
    return duration;
  }
  
  // If it's in seconds format, convert to hours and minutes
  if (typeof duration === 'number' || /^\d+$/.test(duration)) {
    const totalSeconds = parseInt(duration);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }
  
  // If it's in "MM:SS" or "HH:MM:SS" format
  if (typeof duration === 'string' && duration.includes(':')) {
    const parts = duration.split(':');
    if (parts.length === 2) {
      // MM:SS format
      const minutes = parseInt(parts[0]);
      return `${minutes}m`;
    } else if (parts.length === 3) {
      // HH:MM:SS format
      const hours = parseInt(parts[0]);
      const minutes = parseInt(parts[1]);
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      } else {
        return `${minutes}m`;
      }
    }
  }
  
  return duration;
};

// Fallback data when API is not available
const fallbackVideos = [
	{ id: 'PQJ49FGa3CQ', title: 'The Wedding Party', category: 'Trending Nollywood', year: 2016, duration: '2h 10m', views: '2.1M', publishedDate: '2016-12-16T00:00:00Z', description: 'A romantic comedy about a wedding that goes wrong' },
	{ id: '1o0t-68TAPY', title: 'New Nollywood Release', category: 'Trending Nollywood', year: 2024, duration: '1h 45m', views: '1.5M', publishedDate: '2024-01-15T00:00:00Z', description: 'Latest Nollywood movie with amazing storyline' },
	{ id: 'WYmfzX_u6i8', title: 'Latest Nollywood Hit', category: 'Trending Nollywood', year: 2024, duration: '2h 15m', views: '1.2M', publishedDate: '2024-02-20T00:00:00Z', description: 'Another great Nollywood production' },
	{ id: 'dQw4w9WgXcQ', title: 'King of Boys', category: 'Curated Collections', year: 2018, duration: '2h 49m', views: '1.8M', publishedDate: '2018-10-26T00:00:00Z', description: 'A crime thriller about power and politics' },
	{ id: 'dQw4w9WgXcQ', title: 'Lionheart', category: 'Curated Collections', year: 2018, duration: '1h 35m', views: '1.2M', publishedDate: '2018-09-07T00:00:00Z', description: 'A comedy-drama about family business' },
	{ id: 'dQw4w9WgXcQ', title: 'The Figurine', category: 'Golden Era Classics', year: 2009, duration: '2h 2m', views: '900K', publishedDate: '2009-10-30T00:00:00Z', description: 'A supernatural thriller about ancient artifacts' },
	{ id: 'dQw4w9WgXcQ', title: 'Half of a Yellow Sun', category: 'Golden Era Classics', year: 2013, duration: '1h 51m', views: '750K', publishedDate: '2013-09-13T00:00:00Z', description: 'A historical drama set during the Biafran War' },
	{ id: 'dQw4w9WgXcQ', title: 'October 1st', category: 'Fan Favorites', year: 2014, duration: '1h 44m', views: '680K', publishedDate: '2014-10-01T00:00:00Z', description: 'A crime thriller set in colonial Nigeria' },
	{ id: 'dQw4w9WgXcQ', title: 'The CEO', category: 'Fan Favorites', year: 2016, duration: '1h 45m', views: '530K', publishedDate: '2016-07-15T00:00:00Z', description: 'A comedy about corporate life and ambition' },
	{ id: 'dQw4w9WgXcQ', title: '93 Days', category: 'Fan Favorites', year: 2016, duration: '2h 4m', views: '600K', publishedDate: '2016-09-13T00:00:00Z', description: 'A biographical drama about the Ebola crisis' },
];

// Default interaction mode - can be changed via UI
const DEFAULT_INTERACTION_MODE = "modal"; // "expand" | "modal" | "page"

const Section = ({ title, items, onPlay, onExpand, expandedCard, interactionMode, isFirstSection = false }) => (
	<section className="mb-12">
		<h3 className="text-2xl md:text-3xl font-semibold mb-6">{title}</h3>
		<div className="relative">
			{interactionMode === "expand" ? (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
					{items.map((v, index) => {
						// For first section, load first 4-6 cards immediately (above the fold)
						// For other sections, use lazy loading
						const shouldLoadImmediately = isFirstSection && index < 6;
						
						return (
							<LazyMovieCard
								key={v.id + v.title}
								video={v}
								interactionMode={interactionMode}
								onPlay={onPlay}
								onExpand={onExpand}
								isExpanded={expandedCard === v.id}
								shouldLoadImmediately={shouldLoadImmediately}
							/>
						);
					})}
				</div>
			) : (
				<div className="flex gap-6 overflow-x-auto no-scrollbar pb-2">
					{items.map((v, index) => {
						// For horizontal scrolling, load first 3-4 cards immediately for first section
						const shouldLoadImmediately = isFirstSection && index < 4;
						
						return (
							<LazyMovieCard
								key={v.id + v.title}
								video={v}
								interactionMode={interactionMode}
								onPlay={onPlay}
								onExpand={onExpand}
								className="shrink-0 w-[280px] md:w-[320px] lg:w-[280px] xl:w-[300px]"
								shouldLoadImmediately={shouldLoadImmediately}
							/>
						);
					})}
				</div>
			)}
		</div>
	</section>
);

const Home = () => {
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [active, setActive] = useState(null);
	const [muted, setMuted] = useState(true);
	const [heroIndex, setHeroIndex] = useState(0);
	
	// Dynamic data states
	const [, setAllVideos] = useState(fallbackVideos);
	const [collections, setCollections] = useState({});
	const [heroMovies, setHeroMovies] = useState([]);
	const [loading, setLoading] = useState(true);
	
	// Interaction mode states
	const [interactionMode] = useState(DEFAULT_INTERACTION_MODE);
	const [expandedCard, setExpandedCard] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalVideo] = useState(null);
	const [notifyModalOpen, setNotifyModalOpen] = useState(false);

	// Admin slider + category collections override heuristics when configured in Supabase
	useEffect(() => {
		const loadMovies = async () => {
			try {
				setLoading(true);

				const {
					fetchMovies,
					getRandomizedHeroMovies,
					createCollections,
					fetchHomepageSliderMovies,
					fetchHomepageCollectionsFromAdmin
				} = await import('../services/movieApi');

				const [movies, sliderHero, adminCollections] = await Promise.all([
					fetchMovies(),
					fetchHomepageSliderMovies(),
					fetchHomepageCollectionsFromAdmin()
				]);

				// Hero: admin homepage slider always wins when it has items
				if (sliderHero && sliderHero.length > 0) {
					setHeroMovies(sliderHero);
				} else if (movies && movies.length > 0) {
					const randomizedHero = getRandomizedHeroMovies(movies, 3);
					setHeroMovies(
						randomizedHero.length > 0 ? randomizedHero : movies.slice(0, 3)
					);
				} else {
					setHeroMovies(fallbackVideos.slice(0, 3));
				}

				// Collections: if admin has defined categories, use only that (no keyword buckets)
				if (adminCollections.adminMode) {
					setCollections(adminCollections.collections);
				} else if (movies && movies.length > 0) {
					setCollections(createCollections(movies));
				} else {
					setCollections(createCollections(fallbackVideos));
				}

				setAllVideos(
					movies && movies.length > 0 ? movies : fallbackVideos
				);
			} catch (error) {
				console.error('❌ Error fetching movies:', error);
				setAllVideos(fallbackVideos);
				const { createCollections } = await import('../services/movieApi');
				setCollections(createCollections(fallbackVideos));
				setHeroMovies(fallbackVideos.slice(0, 3));
			} finally {
				setLoading(false);
			}
		};

		loadMovies();
	}, []);

	useEffect(() => {
		setHeroIndex(0);
	}, [heroMovies]);

	useEffect(() => {
		if (heroMovies.length > 0) {
			const id = setInterval(() => {
				setHeroIndex((i) => (i + 1) % heroMovies.length);
			}, 20000); // 20s per slide
			return () => clearInterval(id);
		}
	}, [heroMovies]);

	const featured =
		heroMovies[heroIndex] || heroMovies[0] || fallbackVideos[0];

	// Interaction mode handlers
	const handleCardPlay = (video) => {
		// Navigate to dedicated player page
		navigate(`/player/${video.id}`);
	};

	const handleCardExpand = (video) => {
		if (interactionMode === "expand") {
			setExpandedCard(expandedCard === video.id ? null : video.id);
		}
	};

	const handlePlay = (video) => {
		setActive(video);
		setOpen(true);
	};

	// Show loading state
	if (loading) {
		return (
			<main className="pb-16 min-h-screen flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-purple mx-auto mb-4"></div>
					<p className="text-white/80">Loading movies...</p>
				</div>
			</main>
		);
	}

	const structuredData = {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		name: 'Noilio',
		description: 'Discover, stream, and share the best of Nollywood in one place',
		url: typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : 'https://noilio.com',
		potentialAction: {
			'@type': 'SearchAction',
			target: {
				'@type': 'EntryPoint',
				urlTemplate: typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}/search?q={search_term_string}` : 'https://noilio.com/search?q={search_term_string}'
			},
			'query-input': 'required name=search_term_string'
		}
	};

	return (
		<>
			<SEO
				title="Noilio - Nollywood, Reimagined | Stream Nigerian Movies Online"
				description="Discover, stream, and share the best of Nollywood in one place. Watch trending Nollywood movies, curated collections, and fan favorites. Your gateway to African cinema."
				keywords="Nollywood, Nigerian movies, African cinema, streaming, watch movies online, Nollywood films, Nigerian entertainment, African films"
				url="/"
				structuredData={structuredData}
			/>
			<main className="pb-16">
			{/* Hero: 100vh autoplay video slider with overlay */}
			<section className="relative h-screen overflow-hidden">
				<div className="absolute inset-0">
					{/* Single video player for all screen sizes */}
					<div className="absolute inset-0 overflow-hidden hero-video-container">
						<LazyReactPlayer
							key={featured.id}
							url={`https://www.youtube.com/watch?v=${featured.id}`}
							width="100vw"
							height="100vh"
							playing
							muted={muted}
							playsinline
							config={{ 
								youtube: { 
									playerVars: { 
										autoplay: 1,
										controls: 0,
										showinfo: 0,
										rel: 0,
										iv_load_policy: 3,
										modestbranding: 1,
										disablekb: 1,
										fs: 0,
										start: 60, // Start at 1 minute
										end: 20
									} 
								} 
							}}
							className="absolute inset-0 hero-video"
							style={{ 
								position: 'absolute',
								top: '50%',
								left: '50%',
								transform: 'translate(-50%, -50%)',
								minWidth: '100%',
								minHeight: '100%',
								width: 'auto',
								height: 'auto',
								objectFit: 'cover'
							}}
						/>
					</div>
				</div>
				<div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
				<div className="relative z-10 container mx-auto px-4 flex items-end h-screen pb-16 md:pb-10 hero-content">
					<div className="max-w-2xl">
						<h1 className="hero-title text-2xl sm:text-3xl md:text-4xl font-bold mb-2 md:mb-3 animate-fadeIn">
							{featured.title}
						</h1>
						<div className="text-white/80 mb-2 text-sm md:text-base flex items-center gap-2 md:gap-4 flex-wrap">
							<span>{featured.year || '2016'}</span>
							<span className="text-white/40">•</span>
							<span>{formatDuration(featured.duration)}</span>
							<span className="text-white/40">•</span>
							<button
								onClick={() => {
									const channelName = featured.channelName || featured.channel_name || 'Nollywood Studios';
									navigate(`/channel/${encodeURIComponent(channelName)}`);
								}}
								className="text-white/80 hover:text-primary-gold transition-colors underline decoration-transparent hover:decoration-primary-gold"
							>
								{featured.channelName || featured.channel_name || 'Nollywood Studios'}
							</button>
						</div>
						<p className="text-white/80 mb-3 md:mb-4 text-sm md:text-base line-clamp-2">
							{featured.description ? featured.description.substring(0, 120) + '...' : 'A curated Nollywood favorite. Stream instantly and explore more collections.'}
						</p>
						<div className="flex items-center gap-2 md:gap-3 flex-wrap">
							<button 
								className="px-4 md:px-6 py-2 md:py-3 rounded-xl bg-white text-black font-semibold inline-flex items-center gap-2 text-sm md:text-base hover:bg-white/90 transition-colors transform hover:scale-105" 
								onClick={() => navigate(`/player/${featured.id}`)}
							>
								<Play size={16} className="md:w-[18px] md:h-[18px]" />
								Watch Now
							</button>
							<button onClick={() => setMuted(m => !m)} className="px-2 md:px-3 py-2 md:py-3 rounded-xl bg-white/15 border border-white/20 text-white">
								{muted ? <VolumeX size={16} className="md:w-[18px] md:h-[18px]" /> : <Volume2 size={16} className="md:w-[18px] md:h-[18px]" />}
							</button>
							<button 
								className="px-3 md:px-4 py-2 md:py-3 rounded-xl bg-white/10 border border-white/20 text-white font-medium inline-flex items-center gap-2 hover:bg-white/20 transition-colors text-sm md:text-base"
								onClick={() => setNotifyModalOpen(true)}
							>
								<Bell size={14} className="md:w-4 md:h-4" />
								<span className="hidden sm:inline">Notify Me</span>
							</button>
						</div>
						{/* Slider dots */}
						<div className="mt-6 flex items-center gap-2">
							{heroMovies.map((v, i) => (
								<button key={v.id} onClick={() => setHeroIndex(i)} className={`h-1.5 rounded-full transition-all ${i === heroIndex ? 'w-8 bg-white' : 'w-3 bg-white/50'}`} />
							))}
						</div>
					</div>
				</div>
			</section>

			{/* Collections */}
			<section className="container mx-auto px-4 mt-8">
				{Object.entries(collections).map(([title, items], index) => {
					// Insert "Your Reel" after the first collection (works for admin categories or heuristics)
					const shouldShowYourReel = index === 0 && hasRecentlyViewed();
					const yourReelCollection = shouldShowYourReel ? getRecentlyViewedCollection() : null;
					const isFirstSection = index === 0; // Mark the first collection as above-the-fold
					
					return (
						<div key={title}>
							<div className="collection-container mb-8">
								<Section 
									title={title} 
									items={items} 
									onPlay={handleCardPlay}
									onExpand={handleCardExpand}
									expandedCard={expandedCard}
									interactionMode={interactionMode}
									isFirstSection={isFirstSection}
								/>
							</div>
							
							{/* Insert "Your Reel" collection after Trending Nollywood */}
							{shouldShowYourReel && yourReelCollection && (
								<div className="collection-container mb-8">
									<Section 
										title={yourReelCollection.title} 
										items={yourReelCollection.items} 
										onPlay={handleCardPlay}
										onExpand={handleCardExpand}
										expandedCard={expandedCard}
										interactionMode={interactionMode}
										isFirstSection={false} // Your Reel is not the first section
									/>
								</div>
							)}
						</div>
					);
				})}
			</section>

			{/* Ad banner after collections */}
			<section className="container mx-auto px-4 my-10">
				<div className="h-[30vh] w-full rounded-2xl border border-white/10 bg-gradient-to-r from-primary-purple/30 via-black/10 to-primary-gold/20 backdrop-blur-md flex items-center justify-center text-center">
					<div>
						<p className="text-white/80 text-sm uppercase tracking-widest">Sponsored</p>
						<h3 className="text-2xl md:text-3xl font-semibold">Your Ad Could Be Here</h3>
						<p className="text-white/70">Promote new releases, festivals, and cultural moments to the Noilio tribe.</p>
					</div>
				</div>
			</section>

			<Suspense fallback={<div />}>
				<ModalPlayer open={open} onOpenChange={setOpen} youtubeId={active?.id} title={active?.title} />
			</Suspense>
			<Suspense fallback={<div />}>
				<MovieModal 
					isOpen={modalOpen} 
					onClose={() => setModalOpen(false)} 
					video={modalVideo} 
					onPlay={handlePlay} 
				/>
			</Suspense>
			<Suspense fallback={<div />}>
				<NotifyMeModal 
					isOpen={notifyModalOpen} 
					onClose={() => setNotifyModalOpen(false)} 
				/>
			</Suspense>
		</main>
		</>
	);
};

export default Home; 